import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import { formatMoney, formatDate, refundSummary, invoicePill } from "../dashboardHelpers";
import { INVOICE_PILL_COLOR, INVOICE_PILL_LABEL } from "../dashboardConstants";

function SummaryRow({ label, value, bold }) {
  return (
    <Box display="flex" justifyContent="space-between" py={0.5}>
      <Typography variant="body2" fontWeight={bold ? 600 : 400} color={bold ? "text.primary" : "textSecondary"}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={bold ? 600 : 500}>
        {value}
      </Typography>
    </Box>
  );
}

function InvoiceDetailsDialog({ state, currency, onClose, onPayNow, payLoadingId }) {
  const { open, invoice, loading } = state;
  const isUnpaid = invoice?.status === "UNPAID";
  const cur = invoice?.currency || currency;
  // A refund's totals net to 0 for wallet and package refunds and its payment lines name
  // the wallet's underlying voucher, so it is summarised by what came back instead.
  const refund = invoice?.refund;
  // A write-off is stored the same way but gave nothing back — the client never paid.
  const isWriteoff = refund?.destination === "WRITEOFF";
  const pill = invoicePill(invoice);
  // What the club let go of on a written-off invoice: whatever was still owed when it closed.
  const writtenOffAmount =
    invoice?.status === "WRITEOFF" && !refund ? Math.max(0, (parseFloat(invoice.total) || 0) - (parseFloat(invoice.amountPaid) || 0)) : 0;
  const refundLabel = isWriteoff ? "Write-off" : "Refund";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography variant="h6" component="span">
            {refund ? refundLabel : "Invoice"} #{invoice?.invoiceNumber || "—"}
          </Typography>
          {invoice?.createdAt && (
            <Typography variant="caption" display="block" color="textSecondary">
              {formatDate(invoice.createdAt)}
              {refund && invoice.refundOfInvoiceNumber && ` · for invoice #${invoice.refundOfInvoiceNumber}`}
            </Typography>
          )}
        </Box>
        {pill && (
          <Chip
            size="small"
            label={INVOICE_PILL_LABEL[pill] || pill}
            color={INVOICE_PILL_COLOR[pill] || "default"}
            variant="outlined"
            sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading || !invoice ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Items
            </Typography>
            {invoice.items?.length ? (
              invoice.items.map((it, i) => (
                <Box
                  key={i}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  py={1}
                  sx={i < invoice.items.length - 1 ? { borderBottom: 1, borderColor: "divider" } : null}
                >
                  <Box minWidth={0} flex={1}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {it.name}
                    </Typography>
                    {(it.startDate || it.location || it.trainer) && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {[it.startDate && formatDate(it.startDate), it.location, it.trainer].filter(Boolean).join(" — ")}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={500} sx={{ ml: 2, whiteSpace: "nowrap" }}>
                    {it.quantity > 1 ? `${it.quantity} × ` : ""}
                    {formatMoney(it.price, cur)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="textSecondary">
                No line items
              </Typography>
            )}

            {refund ? (
              <Box mt={2} pt={2} sx={{ borderTop: 1, borderColor: "divider" }}>
                <SummaryRow label="Refund" value={refundSummary(refund, cur)} bold />
              </Box>
            ) : (
              <>
                <Box mt={2} pt={2} sx={{ borderTop: 1, borderColor: "divider" }}>
                  <SummaryRow label="Subtotal" value={formatMoney(invoice.subtotal ?? invoice.total, cur)} />
                  {invoice.taxAmount > 0 && (
                    <SummaryRow label={`Tax${invoice.taxRate ? ` (${invoice.taxRate}%)` : ""}`} value={formatMoney(invoice.taxAmount, cur)} />
                  )}
                  <SummaryRow label="Total" value={formatMoney(invoice.total, cur)} bold />
                  {invoice.amountPaid > 0 && <SummaryRow label="Paid" value={formatMoney(invoice.amountPaid, cur)} />}
                  {invoice.amountRemaining > 0 && <SummaryRow label="Remaining" value={formatMoney(invoice.amountRemaining, cur)} />}
                  {writtenOffAmount > 0 && <SummaryRow label="Written off — not charged" value={formatMoney(writtenOffAmount, cur)} />}
                </Box>

                {invoice.payments?.length > 0 && (
                  <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Payments
                    </Typography>
                    {invoice.payments.map((p, i) => (
                      <SummaryRow
                        key={i}
                        label={p.method
                          .replace(/^PAY_BY_/, "")
                          .replace(/_/g, " ")
                          .toLowerCase()
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                        value={formatMoney(p.amount, cur)}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {isUnpaid && invoice && (
          <Button
            variant="contained"
            disabled={payLoadingId === invoice._id}
            onClick={() => onPayNow(invoice._id)}
            sx={{ textTransform: "none", color: "common.white" }}
          >
            {payLoadingId === invoice._id ? <CircularProgress size={18} /> : "Pay Now"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default InvoiceDetailsDialog;
